// src/theme.js
// Unified design tokens + FX for the whole site (Navbar, Footer, pages & games).
// Palette: Neon Avenue v1 — Aqua (#00F5FF) / Violet (#9C4DFF) / Pink (#FF2BC2)
// with Gold (#FFD95E) & Tangerine (#FF9535) accents. Background #070316.

// ---------------- ASSET IMPORTS (resolved from src/assets) ----------------
import AmbientGridURL        from './assets/ambient-grid-6.svg';
import AuraTimerURL          from './assets/aura-timer.svg';
import ButtonGlintURL        from './assets/button-glint-6.svg';
import ButtonPulseURL        from './assets/button-pulse-6.svg';
import CardGlowURL           from './assets/card-glow-6.svg';
import ConfettiStreamURL     from './assets/confetti-stream.svg';
import ConfettiStripURL      from './assets/confetti-strip-6.svg';
import CornerOrbURL          from './assets/corner-orb-6.svg';
import DividerPlasmaURL      from './assets/divider-plasma-6.svg';
import EthCoinSpinURL        from './assets/eth-coin-spin.svg';
import FocusUnderlineURL     from './assets/focus-underline-6.svg';
import GridSheenURL          from './assets/grid-sheen-6.svg';
import GridWaveBgURL         from './assets/grid-wave-bg.svg';
import HoneyCirclePNG        from './assets/honey-circle.png';
import HoneyDripDividerURL   from './assets/honey-drip-divider.svg';
import ProgressShineURL      from './assets/progress-shine-6.svg';
import SectionRadialURL      from './assets/section-radial-6.svg';
import RocketThrusterURL     from './assets/rocket-thruster.svg';
import SparklesURL           from './assets/sparkles-6.svg';
import SparklesAmbientURL    from './assets/sparkles-ambient-6.svg';
import SparklesBurstURL      from './assets/sparkles-burst.svg';
import BgCoinflipLocalURL    from './assets/bg-coinflip-arcs.svg';

// ---------------- PUBLIC helpers (served from /public) ----------------
const safeJoinPublic = (base, path) => {
  const b = (base || '').replace(/\/+$/, '');
  const p = String(path || '').replace(/^\/+/, '');
  return `${b}/${p}`.replace(/^\/+/, '/');
};

// CRA exposes process.env.PUBLIC_URL; Vite exposes import.meta.env.BASE_URL
const PUBLIC_BASE =
  (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL) ||
  '';

const DividerRainbowThinURL   = safeJoinPublic(PUBLIC_BASE, '/divider-rainbow-thin.svg');
const DividerRainbowThin1URL  = safeJoinPublic(PUBLIC_BASE, '/divider-rainbow-thin-1.svg');
const DividerRainbowThin2URL  = safeJoinPublic(PUBLIC_BASE, '/divider-rainbow-thin-2.svg');

// Per‑game card backgrounds (PUBLIC assets)
const BgCrashURL     = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-crash-grid.svg');
const BgCoinflipURL  = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-coinflip-arcs.svg');
const BgDiceURL      = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-dice-pips.svg');
const BgPlinkoURL    = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-plinko-pegs.svg');
const BgMinesURL     = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-mines-hex.svg');
const BgRouletteURL  = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-roulette-radial.svg');
const BgSlotsURL     = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-slots-reels.svg');
const BgHiloURL      = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-hilo-suits.svg');
const BgKenoURL      = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-keno-balls.svg');
const BgCoinsURL     = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-coins-rings.svg');
const BgHoneypotURL  = safeJoinPublic(PUBLIC_BASE, '/assets/bg/bg-honeypot-honeycomb.svg');

// -----------------------------------------------------------------------------
// THEME TOKENS
// -----------------------------------------------------------------------------
const theme = {
  colors: {
    // Foundations (violet‑black world with cool glass tints)
    background: '#070316',
    surface:    'rgba(230,222,255,0.04)',
    surfaceAlt: 'rgba(230,222,255,0.03)',
    card:       'rgba(230,222,255,0.07)',
    border:     'rgba(230,222,255,0.16)',

    // Typography
    text:    '#FFFFFF',
    subText: '#C9C3E3',

    // Brand — Neon Aqua core with Violet secondary
    primary:       '#00F5FF',
    primaryHover:  '#3DF8FF',
    primaryActive: '#00CBD8',
    primarySoft:   'rgba(0,245,255,0.14)',

    secondary:       '#9C4DFF',
    secondaryHover:  '#B074FF',

    // Accents
    accent:         '#FF2BC2',     // Neon Pink
    accentHover:    '#FF69D5',
    accentGold:     '#FFD95E',
    accentGoldHover:'#FFE27E',

    // States (base + hover + text for button/badge use)
    success:      '#37E78C',
    successHover: '#2BC975',
    successText:  '#06110B',
    warning:      '#FFC234',
    warningText:  '#261A00',
    danger:       '#FF4D4D',
    dangerHover:  '#FF3333',

    // Links & focus
    link:       '#00F5FF',
    linkHover:  '#3DF8FF',
    focus:      'rgba(0,245,255,0.80)',

    // Overlays
    overlay: 'rgba(7,3,22,0.60)',
  },

  // Radii — 6px everywhere for consistency
  radii: { card: '6px', chip: '6px', button: '6px', pill: '999px' },

  // Shadows — tuned to palette
  shadows: {
    glowPrimary: `0 0 28px rgba(0,245,255,0.26)`,
    glowCyan:    `0 0 32px rgba(127,243,236,0.32)`,
    glowViolet:  `0 0 28px rgba(156,77,255,0.28)`,
    glowPink:    `0 0 24px rgba(255,43,194,0.28)`,
    soft:        '0 8px 24px rgba(0,0,0,0.28)',
  },

  // “Glass” slab tokens (used by Home’s GlassPanel, etc.)
  glass: {
    stroke: 'rgba(255,255,255,0.18)',
    shadow: '0 10px 28px rgba(0,0,0,0.35)',
  },

  blur: { glass: '12px' },
  z: { bar: 1300, mobileCta: 1299 },

  // Logo recolor controls (Navbar uses these to tint the logo art)
  logo: {
    hueRotate:   '210deg',
    saturate:    1.25,
    glowShadow:  '0 0 18px rgba(0,245,255,0.45), 0 0 12px rgba(156,77,255,0.28)',
    mixBlendMode:'screen',
    // Navbar-specific tint override (optional). Used by `.logo-tint--navbar` class.
    navbar: {
      hueRotate: '205deg',
      saturate:  1.35,
      glowShadow: '0 0 18px rgba(0,245,255,0.55), 0 0 12px rgba(156,77,255,0.32)',
      mixBlendMode: 'screen',
      tintColor: '#00E0F0', // solid color used by mask-based logo tint
    },
  },

  // Gradients for buttons/hero & jackpot ribbons
  gradients: {
    primary:   'linear-gradient(135deg, #00E0F0 0%, #0BA3E6 28%, #2F3E9E 62%, #6E3DF7 100%)',
    secondary: 'linear-gradient(135deg, #6E3DF7 0%, #2F3E9E 44%, #00E0F0 100%)',
    jackpot:   'linear-gradient(135deg, #FFD95E 0%, #FF2BC2 50%, #9C4DFF 100%)',
    accent:    'linear-gradient(135deg, #FF2BC2 0%, #9C4DFF 100%)',
    success:   'linear-gradient(135deg, #37E78C 0%, #00F5FF 100%)',
  },

  typography: {
    // System font stacks for crisp rendering across platforms
    fontFamily: `Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji"`,
    fontFamilyMono: `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`,

    // Display
    h1: { fontWeight: 900, letterSpacing: 0.4, fontSize: 'clamp(36px, 6.5vw, 72px)', lineHeight: 1.08 },
    h2: { fontWeight: 800, letterSpacing: 0.2, fontSize: 'clamp(28px, 4.5vw, 48px)', lineHeight: 1.15 },
    h3: { fontWeight: 800, letterSpacing: 0.2, fontSize: 'clamp(22px, 3.2vw, 36px)', lineHeight: 1.18 },

    // Body & meta
    body1:   { fontSize: '1rem', lineHeight: 1.65 },
    body2:   { fontSize: '0.9375rem', lineHeight: 1.6 },
    subtitle1: { fontWeight: 600, fontSize: '1.05rem', letterSpacing: 0.2, lineHeight: 1.4 },
    caption: { fontSize: '0.8125rem', letterSpacing: 0.3, opacity: 0.9 },

    // UI chips/badges
    chip:  { fontWeight: 800, fontSize: 12, letterSpacing: 0.4 },
  },

  // Motion & timing
  transitions: {
    fast: '120ms',
    normal: '220ms',
    slow: '360ms',
    easing: {
      standard: 'cubic-bezier(0.2, 0.0, 0.2, 1)',
      emphasized: 'cubic-bezier(0.2, 0.0, 0, 1)',
      entrance: 'cubic-bezier(0, 0, 0.2, 1)',
      exit: 'cubic-bezier(0.4, 0.0, 1, 1)',
    },
  },

  // Spacing scale (8pt base)
  spacing: {
    xs: 4, sm: 8, md: 12, lg: 16, xl: 24, '2xl': 32, '3xl': 48,
  },

  // Breakpoints (px)
  breakpoints: {
    xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536,
  },

  // VIP tiers: distinct, unmistakable colors + premium gradients
  vip: {
    palette:  { shrimp:'#6E7A8A', fish:'#00F5FF', dolphin:'#9C4DFF', whale:'#FF2BC2' },
    gradients:{
      shrimp:  'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
      fish:    'linear-gradient(135deg, #00F5FF 0%, #0094FF 100%)',
      dolphin: 'linear-gradient(135deg, #9C4DFF 0%, #00F5FF 100%)',
      whale:   'linear-gradient(135deg, #FF2BC2 0%, #9C4DFF 50%, #00F5FF 100%)',
    },
  },

  // Layout and depth tokens
  layout: {
    containerMax: '1200px',
    sectionPad: 'clamp(32px, 8vw, 96px)',
  },
  elevation: {
    1: '0 4px 12px rgba(0,0,0,0.25)',
    2: '0 8px 24px rgba(0,0,0,0.28)',
    3: '0 14px 40px rgba(0,0,0,0.35)',
  },
  buttons: {
    minHeight: 44,
    fontWeight: 900,
    letterSpacing: 0.3,
  },

  // ---------------------------------------------------------------------------
  // FX: Decorative motion + ambient assets (all controllable via CSS vars)
  // ---------------------------------------------------------------------------
  fx: {
    motionScale: 1,       // synced to prefers-reduced-motion in index.js
    blendMode: 'screen',

    // Interactive
    buttonGlint:   { url: ButtonGlintURL,   opacity: 0,    hoverOpacity: 0.75 },
    buttonPulse:   { url: ButtonPulseURL,   opacity: 0.25 },
    progressShine: { url: ProgressShineURL, opacity: 1,    speedSec: 3.2 },
    cardGlow:      { url: CardGlowURL,      opacity: 0.22 },

    // Ambient/background
    gridWave:        { url: GridWaveBgURL,      opacity: 0.12, size: 'cover',        position: 'center -10vh', repeat: 'no-repeat' },
    ambientGrid:     { url: AmbientGridURL,     opacity: 0.12, size: '1100px auto',  position: 'center',       repeat: 'repeat' },
    sparklesAmbient: { url: SparklesAmbientURL, opacity: 0.18, size: '600px auto',   position: 'center',       repeat: 'repeat' },
    gridSheen:       { url: GridSheenURL,       opacity: 0.12 },
    sectionRadial:   { url: SectionRadialURL,   opacity: 0.20 },
    dividerPlasma:   { url: DividerPlasmaURL,   opacity: 1.00 },
    honeyDrip:       { url: HoneyDripDividerURL,opacity: 1.00 },
    cornerOrb:       { url: CornerOrbURL,       opacity: 0.50 },
    focusUnderline:  { url: FocusUnderlineURL,  opacity: 1.00 },

    // Celebration / micro FX
    confettiStream:  { url: ConfettiStreamURL,  opacity: 1.00 },
    confettiStrip:   { url: ConfettiStripURL,   opacity: 1.00 },
    sparkles:        { url: SparklesURL,        opacity: 0.90 },
    sparklesBurst:   { url: SparklesBurstURL,   opacity: 1.00 },

    // Thematic extras
    ethCoinSpin:     { url: EthCoinSpinURL,     opacity: 1.00 },
    rocket:          { url: RocketThrusterURL,  opacity: 1.00 },
    auraTimer:       { url: AuraTimerURL,       opacity: 0.85 },
    honeyCircle:     { url: HoneyCirclePNG,     opacity: 1.00, size: 'contain' },

    // Thin rainbow divider (public path)
    dividerRainbowThin: {
      url:  DividerRainbowThinURL,
      url1: DividerRainbowThin1URL,
      url2: DividerRainbowThin2URL,
      opacity: 1.0,
      height: '2px',
    },
  },
};

export default theme;

// -----------------------------------------------------------------------------
// CSS VARIABLES EMITTER
// -----------------------------------------------------------------------------
export const toCssVars = (t = theme) => ({
  // Foundations
  '--color-background': t.colors.background,
  '--color-surface': t.colors.surface,
  '--color-surfaceAlt': t.colors.surfaceAlt,
  '--color-card': t.colors.card,
  '--color-border': t.colors.border,

  // Typography
  '--color-text': t.colors.text,
  '--color-subText': t.colors.subText,

  // Brand
  '--color-primary': t.colors.primary,
  '--color-primaryHover': t.colors.primaryHover,
  '--color-primaryActive': t.colors.primaryActive,
  '--color-secondary': t.colors.secondary,
  '--color-secondaryHover': t.colors.secondaryHover,

  // Accents
  '--color-accent': t.colors.accent,
  '--color-accentHover': t.colors.accentHover,
  '--color-accentGold': t.colors.accentGold,
  '--color-accentGoldHover': t.colors.accentGoldHover,

  // States
  '--color-success': t.colors.success,
  '--color-warning': t.colors.warning,
  '--color-danger': t.colors.danger,

  // Links & focus
  '--color-link': t.colors.link,
  '--color-linkHover': t.colors.linkHover,
  '--color-focus': t.colors.focus,

  // Fonts
  '--font-sans': t.typography?.fontFamily ?? 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  '--font-mono': t.typography?.fontFamilyMono ?? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',

  // Optional label overrides (index.css reads these if present)
  '--button-label-primary': '#FFFFFF',
  '--button-label-secondary': '#FFFFFF',

  // Radii
  '--radius-card': t.radii.card,
  '--radius-chip': t.radii.chip,
  '--radius-button': t.radii.button,
  '--radius-pill': t.radii.pill,

  // Shadows
  '--shadow-glowPrimary': t.shadows.glowPrimary,
  '--shadow-glowCyan':    t.shadows.glowCyan,
  '--shadow-glowViolet':  t.shadows.glowViolet,
  '--shadow-glowPink':    t.shadows.glowPink,
  '--shadow-soft':        t.shadows.soft,

  // Blur
  '--blur-glass': t.blur.glass,

  // Logo
  '--logo-hueRotate': t.logo.hueRotate,
  '--logo-saturate': String(t.logo.saturate),
  '--logo-glowShadow': t.logo.glowShadow,
  '--logo-mixBlendMode': t.logo.mixBlendMode,

  // Logo (Navbar override)
  '--logo-navbar-hueRotate':    (t.logo?.navbar?.hueRotate ?? t.logo.hueRotate),
  '--logo-navbar-saturate':     String(t.logo?.navbar?.saturate ?? t.logo.saturate),
  '--logo-navbar-glowShadow':   (t.logo?.navbar?.glowShadow ?? t.logo.glowShadow),
  '--logo-navbar-mixBlendMode': (t.logo?.navbar?.mixBlendMode ?? t.logo.mixBlendMode),
  '--logo-navbar-tintColor':   (t.logo?.navbar?.tintColor ?? '#00E0F0'),

  // Gradients
  '--grad-primary':   t.gradients.primary,
  '--grad-secondary': t.gradients.secondary,
  '--grad-jackpot':   t.gradients.jackpot,

  // VIP
  '--vip-shrimp':  t.vip?.palette.shrimp  || '#6E7A8A',
  '--vip-fish':    t.vip?.palette.fish    || '#00F5FF',
  '--vip-dolphin': t.vip?.palette.dolphin || '#9C4DFF',
  '--vip-whale':   t.vip?.palette.whale   || '#FFD95E',
  '--vip-grad-shrimp':  t.vip?.gradients.shrimp,
  '--vip-grad-fish':    t.vip?.gradients.fish,
  '--vip-grad-dolphin': t.vip?.gradients.dolphin,
  '--vip-grad-whale':   t.vip?.gradients.whale,

  // Helpful ramps for ambient/focus (override index.css fallbacks)
  '--ramp-primary-650': '#3DF8FF',
  '--ramp-primary-600': '#00F5FF',
  '--ramp-primary-550': '#00CBD8',
  '--ramp-primary-500': '#007D79',
  '--ramp-primary-400': '#7FF3EC',
  '--ramp-violet-600':  '#300193',
  '--ramp-violet-520':  '#3F01A0',
  '--ramp-violet-480':  '#4F018D',
  '--ramp-violet-420':  '#6F1DA3',
  '--ramp-magenta-500': '#FF2BC2',
  '--ramp-magenta-450': '#FF69D5',
  '--ramp-glow-100':    '#DAC9E3',

  // --------- RAW ASSET URL VARS ----------
  '--asset-grid-wave':            `url(${t.fx.gridWave?.url || ''})`,
  '--asset-ambient-grid':         `url(${t.fx.ambientGrid?.url || ''})`,
  '--asset-sparkles-ambient':     `url(${t.fx.sparklesAmbient?.url || ''})`,
  '--asset-grid-sheen':           `url(${t.fx.gridSheen?.url || ''})`,
  '--asset-section-radial':       `url(${t.fx.sectionRadial?.url || ''})`,
  '--asset-divider-plasma':       `url(${t.fx.dividerPlasma?.url || ''})`,
  '--asset-honey-drip':           `url(${t.fx.honeyDrip?.url || ''})`,
  '--asset-corner-orb':           `url(${t.fx.cornerOrb?.url || ''})`,
  '--asset-focus-underline':      `url(${t.fx.focusUnderline?.url || ''})`,

  '--asset-button-glint':         `url(${t.fx.buttonGlint?.url || ''})`,
  '--asset-button-pulse':         `url(${t.fx.buttonPulse?.url || ''})`,
  '--asset-progress-shine':       `url(${t.fx.progressShine?.url || ''})`,
  '--asset-card-glow':            `url(${t.fx.cardGlow?.url || ''})`,

  '--asset-sparkles':             `url(${t.fx.sparkles?.url || ''})`,
  '--asset-sparkles-burst':       `url(${t.fx.sparklesBurst?.url || ''})`,
  '--asset-confetti-stream':      `url(${t.fx.confettiStream?.url || ''})`,
  '--asset-confetti-strip':       `url(${t.fx.confettiStrip?.url || ''})`,

  '--asset-eth-coin-spin':        `url(${t.fx.ethCoinSpin?.url || ''})`,
  '--asset-rocket-thruster':      `url(${t.fx.rocket?.url || ''})`,
  '--asset-aura-timer':           `url(${t.fx.auraTimer?.url || ''})`,
  '--asset-honey-circle':         `url(${t.fx.honeyCircle?.url || ''})`,

  // Per‑game card backgrounds (PUBLIC)
  '--asset-bg-crash':     `url(${BgCrashURL})`,
  '--asset-bg-coinflip':  `url(${BgCoinflipLocalURL || BgCoinflipURL})`,
  '--asset-bg-dice':      `url(${BgDiceURL})`,
  '--asset-bg-plinko':    `url(${BgPlinkoURL})`,
  '--asset-bg-mines':     `url(${BgMinesURL})`,
  '--asset-bg-roulette':  `url(${BgRouletteURL})`,
  '--asset-bg-slots':     `url(${BgSlotsURL})`,
  '--asset-bg-hilo':      `url(${BgHiloURL})`,
  '--asset-bg-keno':      `url(${BgKenoURL})`,
  '--asset-bg-coins':     `url(${BgCoinsURL})`,
  '--asset-bg-honeypot':  `url(${BgHoneypotURL})`,

  // Thin rainbow divider assets (public)
  '--asset-divider-rainbow-thin':   `url(${t.fx.dividerRainbowThin?.url  || DividerRainbowThinURL})`,
  '--asset-divider-rainbow-thin-1': `url(${t.fx.dividerRainbowThin?.url1 || DividerRainbowThin1URL})`,
  '--asset-divider-rainbow-thin-2': `url(${t.fx.dividerRainbowThin?.url2 || DividerRainbowThin2URL})`,

  // --------- FX CONTROLS ----------
  '--fx-motion-scale': String(t.fx.motionScale ?? 1),
  '--fx-blend-mode':   t.fx.blendMode || 'screen',

  // Buttons (consumed by MUI overrides)
  '--fx-button-glint-url':         'var(--asset-button-glint)',
  '--fx-button-glint-opacity':     String(t.fx.buttonGlint?.opacity ?? 0),
  '--fx-button-glint-hover':       String(t.fx.buttonGlint?.hoverOpacity ?? 0.95),
  '--fx-button-pulse-url':         'var(--asset-button-pulse)',
  '--fx-button-pulse-opacity':     String(t.fx.buttonPulse?.opacity ?? 0.55),

  // Progress
  '--fx-progress-shine-url':       'var(--asset-progress-shine)',
  '--fx-progress-shine-opacity':   String(t.fx.progressShine?.opacity ?? 1),
  '--fx-progress-shine-speed':     (t.fx.progressShine?.speedSec ? `${t.fx.progressShine.speedSec}s` : '3.2s'),

  // Cards
  '--fx-card-glow-url':            'var(--asset-card-glow)',
  '--fx-card-glow-opacity':        String(t.fx.cardGlow?.opacity ?? 0.22),

  // Ambient tunables
  '--fx-grid-wave-opacity':        String(t.fx.gridWave?.opacity ?? 0.16),
  '--fx-ambient-grid-opacity':     String(t.fx.ambientGrid?.opacity ?? 0.16),
  '--fx-sparkles-ambient-opacity': String(t.fx.sparklesAmbient?.opacity ?? 0.22),
  '--fx-grid-sheen-opacity':       String(t.fx.gridSheen?.opacity ?? 0.12),
  '--fx-section-radial-opacity':   String(t.fx.sectionRadial?.opacity ?? 0.20),
  '--fx-divider-plasma-opacity':   String(t.fx.dividerPlasma?.opacity ?? 1.00),
  '--fx-honey-drip-opacity':       String(t.fx.honeyDrip?.opacity ?? 1.00),
  '--fx-corner-orb-opacity':       String(t.fx.cornerOrb?.opacity ?? 0.50),
  '--fx-focus-underline-opacity':  String(t.fx.focusUnderline?.opacity ?? 1.00),
  '--fx-sparkles-opacity':         String(t.fx.sparkles?.opacity ?? 0.90),
  '--fx-sparkles-burst-opacity':   String(t.fx.sparklesBurst?.opacity ?? 1.00),

  // Sizes/positions
  '--fx-grid-wave-size':           t.fx.gridWave?.size || 'cover',
  '--fx-grid-wave-position':       t.fx.gridWave?.position || 'center',
  '--fx-grid-wave-repeat':         t.fx.gridWave?.repeat || 'no-repeat',
  '--fx-ambient-grid-size':        t.fx.ambientGrid?.size || '1100px auto',
  '--fx-ambient-grid-position':    t.fx.ambientGrid?.position || 'center',
  '--fx-ambient-grid-repeat':      t.fx.ambientGrid?.repeat || 'repeat',
  '--fx-sparkles-ambient-size':    t.fx.sparklesAmbient?.size || '600px auto',
  '--fx-sparkles-ambient-position':t.fx.sparklesAmbient?.position || 'center',
  '--fx-sparkles-ambient-repeat':  t.fx.sparklesAmbient?.repeat || 'repeat',

  // Motion tokens
  '--motion-fast':   t.transitions?.fast || '120ms',
  '--motion-normal': t.transitions?.normal || '220ms',
  '--motion-slow':   t.transitions?.slow || '360ms',
  '--ease-standard': t.transitions?.easing?.standard || 'cubic-bezier(0.2, 0.0, 0.2, 1)',
  '--ease-emphasized': t.transitions?.easing?.emphasized || 'cubic-bezier(0.2, 0.0, 0, 1)',

  // Spacing tokens
  '--space-xs':  `${t.spacing?.xs ?? 4}px`,
  '--space-sm':  `${t.spacing?.sm ?? 8}px`,
  '--space-md':  `${t.spacing?.md ?? 12}px`,
  '--space-lg':  `${t.spacing?.lg ?? 16}px`,
  '--space-xl':  `${t.spacing?.xl ?? 24}px`,
  '--space-2xl': `${t.spacing?.['2xl'] ?? 32}px`,
  '--space-3xl': `${t.spacing?.['3xl'] ?? 48}px`,

  // Elevation levels
  '--elev-1': t.elevation?.[1] || '0 4px 12px rgba(0,0,0,0.25)',
  '--elev-2': t.elevation?.[2] || '0 8px 24px rgba(0,0,0,0.28)',
  '--elev-3': t.elevation?.[3] || '0 14px 40px rgba(0,0,0,0.35)',

  // Layout helpers
  '--container-max': t.layout?.containerMax || '1200px',
  '--section-pad': t.layout?.sectionPad || 'clamp(32px, 8vw, 96px)',

  // Button defaults
  '--button-minHeight': `${t.buttons?.minHeight ?? 44}px`,
  '--button-fontWeight': String(t.buttons?.fontWeight ?? 900),
  '--button-letterSpacing': `${t.buttons?.letterSpacing ?? 0.3}px`,

  // Selection & scrollbar colors
  '--selection-bg':  t.colors?.primarySoft || 'rgba(0,245,255,0.14)',
  '--selection-fg':  t.colors?.text || '#FFFFFF',
  '--scrollbar-thumb': 'rgba(255,255,255,0.22)',
  '--scrollbar-track': 'rgba(255,255,255,0.06)',
});

// Ensures a global CSS rule exists so Navbar/Drawer logos always obey theme tint
function ensureLogoTintStyle(doc = (typeof document !== 'undefined' ? document : null)) {
  try {
    if (!doc) return;
    const css = `
    /* Enforce tint on common logo targets inside AppBar & Drawer */
    .MuiAppBar-root .logo-tint,
    .MuiAppBar-root img[alt*="logo" i],
    .MuiAppBar-root [aria-label*="logo" i],
    .MuiDrawer-root .logo-tint,
    .MuiDrawer-root img[alt*="logo" i],
    .MuiDrawer-root [aria-label*="logo" i] {
      filter:
        hue-rotate(var(--logo-navbar-hueRotate, var(--logo-hueRotate)))
        saturate(var(--logo-navbar-saturate, var(--logo-saturate)))
        drop-shadow(var(--logo-navbar-glowShadow, var(--logo-glowShadow))) !important;
      mix-blend-mode: var(--logo-navbar-mixBlendMode, var(--logo-mixBlendMode)) !important;
    }

    /* If logo uses the mask-based element, force the navbar tint color */
    .MuiAppBar-root .logo-mask,
    .MuiDrawer-root .logo-mask {
      background: var(--logo-navbar-tintColor, var(--color-primary)) !important;
    }
    `;

    let styleTag = doc.getElementById('logo-tint-enforcer');
    if (!styleTag) {
      styleTag = doc.createElement('style');
      styleTag.id = 'logo-tint-enforcer';
      styleTag.appendChild(doc.createTextNode(css));
      doc.head.appendChild(styleTag);
    } else {
      // keep it up to date with current variables
      if (styleTag.textContent !== css) styleTag.textContent = css;
    }
  } catch {}
}

/* Global utility styles: gradient text, glass, selection, scrollbars */
function ensureGlobalStyles(doc = (typeof document !== 'undefined' ? document : null), t = theme) {
  try {
    if (!doc) return;
    const css = `
    /* Base document styling */
    html, body {
      background: var(--color-background);
      color: var(--color-text);
      text-rendering: optimizeLegibility;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    body {
      font-family: var(--font-sans, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial);
      line-height: 1.6;
    }
    code, kbd, pre, samp {
      font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace);
    }

    /* Layout variable defaults (Navbar/Drawers may override at runtime) */
    :root {
      --topbar-height: 56px;
      --nav-left-width: 280px;
      --chat-right-width: 0px;
    }

    /* Screen-reader only utility */
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }

    /* Reduced motion: soften transforms globally */
    @media (prefers-reduced-motion: reduce) {
      .hover-lift,
      .MuiButtonBase-root,
      .MuiIconButton-root {
        transition: background-color var(--motion-fast) var(--ease-standard) !important;
        transform: none !important;
      }
    }

    /* Subtle ambient background layer */
    body::before {
      content: '';
      position: fixed;
      inset: -10vh -10vw;
      pointer-events: none;
      z-index: 0;
      background-image: var(--asset-grid-wave);
      background-repeat: var(--fx-grid-wave-repeat, no-repeat);
      background-size: var(--fx-grid-wave-size, cover);
      background-position: var(--fx-grid-wave-position, center);
      filter: opacity(var(--fx-grid-wave-opacity, 0.16));
      /* gentle edge vignette */
      -webkit-mask-image: radial-gradient(120% 120% at 50% 40%, rgba(0,0,0,0.9), rgba(0,0,0,0.4) 60%, transparent 100%);
              mask-image: radial-gradient(120% 120% at 50% 40%, rgba(0,0,0,0.9), rgba(0,0,0,0.4) 60%, transparent 100%);
    }

    /* Gradient text helper */
    .gradient-text {
      background: var(--grad-primary);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
    }

    /* Focus visibility: accessible, on-brand */
    :focus-visible {
      outline: none;
      box-shadow:
        0 0 0 2px rgba(7,3,22,0.85),
        0 0 0 4px var(--color-focus);
      transition: box-shadow var(--motion-fast) var(--ease-standard);
    }

    /* Glass panel helper */
    .glass-panel {
      background: var(--color-card);
      border: 1px solid var(--color-border);
      backdrop-filter: blur(var(--blur-glass, 12px));
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-soft);
    }

    /* Elevated surface helper */
    .surface-elevated {
      background: linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02));
      border: 1px solid var(--color-border);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-soft);
    }

    /* Container helpers */
    .container {
      width: min(var(--container-max, 1200px), 100%);
      margin-inline: auto;
      padding-inline: clamp(16px, 4vw, 32px);
    }
    .section {
      padding-block: var(--section-pad, clamp(32px, 8vw, 96px));
    }

    /* Motion scale utility classes */
    .motion-fast   { transition-duration: var(--motion-fast); }
    .motion-normal { transition-duration: var(--motion-normal); }
    .motion-slow   { transition-duration: var(--motion-slow); }

    /* Nice hover lift for cards/buttons */
    .hover-lift {
      transition: transform var(--motion-normal) var(--ease-standard), box-shadow var(--motion-normal) var(--ease-standard);
    }
    .hover-lift:hover {
      transform: translateY(calc(-2px * var(--fx-motion-scale, 1)));
      box-shadow: 0 12px 28px rgba(0,0,0,0.35), var(--shadow-glowCyan);
    }

    /* Selection color */
    ::selection {
      background: var(--selection-bg);
      color: var(--selection-fg);
    }
    ::-moz-selection {
      background: var(--selection-bg);
      color: var(--selection-fg);
    }

    /* Scrollbars */
    * {
      scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
      scrollbar-width: thin;
    }
    *::-webkit-scrollbar {
      height: 10px;
      width: 10px;
    }
    *::-webkit-scrollbar-track {
      background: var(--scrollbar-track);
    }
    *::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb);
      border-radius: 8px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }

    /* Headings: balance long lines for nicer rags */
    h1, h2, h3 { text-wrap: balance; }

    /* Optional kill switch for ambient layers (add 'no-ambient' to html/body) */
    .no-ambient {
      --fx-ambient-grid-opacity: 0 !important;
      --fx-grid-wave-opacity: 0 !important;
      --fx-sparkles-ambient-opacity: 0 !important;
    }

    /* Typographic utilities */
    .text-balance {
      text-wrap: balance;
    }
    .kbd {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 6px;
      border: 1px solid var(--color-border);
      background: rgba(255,255,255,0.06);
      font-family: var(--font-mono);
      font-size: 0.85em;
    }
    `;
    let styleTag = doc.getElementById('global-theme-utils');
    if (!styleTag) {
      styleTag = doc.createElement('style');
      styleTag.id = 'global-theme-utils';
      styleTag.appendChild(doc.createTextNode(css));
      doc.head.appendChild(styleTag);
    } else if (styleTag.textContent !== css) {
      styleTag.textContent = css;
    }
  } catch {}
}

export function applyThemeCssVars(
  root = typeof document !== 'undefined' ? document.documentElement : null,
  t = theme
) {
  if (!root) return;
  const vars = toCssVars(t);
  for (const [k, v] of Object.entries(vars)) {
    root.style.setProperty(k, v);
  }
  // Respect reduced motion preference (downshift motion scale)
  try {
    const doc = root.ownerDocument || (typeof document !== 'undefined' ? document : null);
    const win = doc?.defaultView;
    const mqr = win?.matchMedia?.('(prefers-reduced-motion: reduce)');
    const updateMotionScale = () => {
      const scale = mqr && mqr.matches ? 0 : (t.fx?.motionScale ?? 1);
      root.style.setProperty('--fx-motion-scale', String(scale));
    };
    updateMotionScale();
    if (mqr) {
      if (mqr.addEventListener) {
        mqr.addEventListener('change', updateMotionScale);
      } else if (mqr.addListener) {
        // Safari
        mqr.addListener(updateMotionScale);
      }
    }
  } catch {}

  // After writing CSS variables, inject/update the enforcement style so logos follow theme
  try {
    const doc = root.ownerDocument || (typeof document !== 'undefined' ? document : null);
    ensureLogoTintStyle(doc);
    ensureGlobalStyles(doc, t);
  } catch {}
}
